import { useSearchParams } from '@solidjs/router';
import { createEffect, createSignal, Match, onMount, Show, Switch } from 'solid-js';
import LoginBG from '~/assets/loginBg.png';
import {
  EMAIL_AND_PASSWORD_ARE_REQUIRED,
  EMAIL_IS_REQUIRED,
  PHONE_NUMBER_IS_REQUIRED,
  PLEASE_CHECK_YOUR_EMAIL_FOR_A_MAGIC_LINK_TO_SIGN_IN,
  SIGN_IN_TO_YOUR_ACCOUNT,
  VERIFICATION_CODE_IS_REQUIRED,
} from '~/assets/strings';
import ImageWithFallback from '~/components/common/images/ImageWithFallback';
import usePresenter from '~/framework/hooks/usePresenter';
import useUseCase from '~/framework/hooks/useUseCase';
import { LoginRequest } from '~/gateways/auth/authGateway';
import { useStyling } from '~/global-contexts/CompanyStyling';
import { useLocalization } from '~/hooks/useLocalization';
import { LoggedInStatusPresenter } from '~/presenters/LoggedInStatusPresenter';
import { LoginType } from '~/types/LoginType';
import { IsLoggedInUseCase } from '~/use-cases/auth/isLoggedInUseCase';
import { LogInUseCase } from '~/use-cases/auth/logInUseCase';
import { SendMagicLinkUseCase } from '~/use-cases/auth/sendMagicLinkUseCase';
import { SendPhoneVerificationCodeUseCase } from '~/use-cases/auth/sendPhoneVerificationCodeUseCase';
import { getCurrentHostName } from '~/utils/host';
import { AccountSignIn } from './components/AccountSignInBox';
import { MagicLinkSignIn } from './components/MagicLinkSignInBox';
import { PhoneNumberSignIn } from './components/PhoneNumberSignInBox';

export const SignIn = () => {
  const { t } = useLocalization();
  const [searchParams] = useSearchParams();
  const [error, setError] = createSignal<string>();
  const [message, setMessage] = createSignal<string>();
  const [loginType, setLoginType] = createSignal<LoginType>(LoginType.Account);

  const { execute: logIn, isLoading: isLoadingLogin } = useUseCase(LogInUseCase);
  const { execute: sendMagicLink, isLoading: isSendingMagicLink, didSucceed: didSendMagicLink } = useUseCase(SendMagicLinkUseCase);
  const { execute: isLoggedIn } = useUseCase(IsLoggedInUseCase);
  const {
    execute: sendPhoneVerificationCode,
    isLoading: isSendingPhoneVerificationCode,
    didSucceed: didSendPhoneVerificationCode,
  } = useUseCase(SendPhoneVerificationCodeUseCase);
  const { model: loginStatus } = usePresenter(LoggedInStatusPresenter);

  const { getCompanyStyling } = useStyling();

  onMount(() => {
    isLoggedIn(searchParams.redirect);
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const email: string = (formData.get('email') as string) || '';
    const password: string = (formData.get('password') as string) || '';
    const phone: string = (formData.get('phone') as string) || '';
    const code: string = (formData.get('verificationCode') as string) || '';

    if (loginType() === LoginType.Account) {
      if (!email?.length || !password?.length) {
        setError(t(EMAIL_AND_PASSWORD_ARE_REQUIRED));
        return;
      }
      logIn({ email, password, domain: getCurrentHostName(), loginType: LoginType.Account });
    } else if (loginType() === LoginType.MagicLink) {
      if (!email?.length) {
        setError(t(EMAIL_IS_REQUIRED));
        return;
      }
      sendMagicLink({ email, domain: getCurrentHostName() });
    } else if (loginType() === LoginType.PhoneNumber) {
      if (didSendPhoneVerificationCode()) {
        if (!code?.length) {
          setError(t(VERIFICATION_CODE_IS_REQUIRED));
          return;
        }
        logIn({ phone, loginToken: code, loginType: LoginType.PhoneNumber, domain: getCurrentHostName() } as LoginRequest);
      } else {
        if (!phone?.length) {
          setError(t(PHONE_NUMBER_IS_REQUIRED));
          return;
        }
        sendPhoneVerificationCode({ phone, domain: getCurrentHostName() });
      }
    }
  };

  createEffect(() => {
    if (didSendMagicLink()) {
      setMessage(t(PLEASE_CHECK_YOUR_EMAIL_FOR_A_MAGIC_LINK_TO_SIGN_IN));
    }
  });

  createEffect(() => {
    if (!isLoadingLogin()) {
      setError(t(loginStatus()?.errorMessage));
    }
  });

  const handleLoginTypeChange = (type: LoginType) => {
    setError(undefined);
    setMessage(undefined);
    setLoginType(type);
  };

  return (
    <div class="grid h-full grid-cols-1 md:grid-cols-2">
      <div class="relative hidden grid-cols-1 flex-col justify-center bg-[#F0F6FB] py-8 md:flex">
        <Show when={getCompanyStyling()?.logo}>
          <div class="absolute left-0 top-0 flex justify-start px-16 py-10">
            <ImageWithFallback
              fallback={null}
              src={getCompanyStyling()?.logo}
              alt="Logo"
              class="aspect-auto size-1/3 rounded-2xl object-cover"
            />
          </div>
        </Show>
        <img src={LoginBG} alt={t('Interior illustration')} class="h-auto w-full" />
      </div>

      <div class="relative flex grid-cols-1 flex-col justify-center bg-white p-6 md:p-8">
        <div class="mx-auto mb-24 w-full max-w-md">
          <div class="mb-14 flex justify-center md:hidden">
            <ImageWithFallback fallback={null} src={getCompanyStyling()?.logo} alt="Logo" class="aspect-auto rounded-2xl object-cover" />
          </div>

          <h1 class="mb-2 text-2xl font-bold text-gray-800 md:text-3xl">
            {t('Welcome to {name}', {
              name: `${getCompanyStyling()?.name}`,
            })}
          </h1>
          <p class="mb-8 text-gray-600">{t(SIGN_IN_TO_YOUR_ACCOUNT)}</p>

          <form onSubmit={handleSubmit} class="flex flex-col gap-6">
            <Switch>
              <Match when={loginType() === LoginType.Account}>
                <AccountSignIn
                  onLoginTypeChange={handleLoginTypeChange}
                  onSubmit={(email, password) => logIn({ email, password, loginType: LoginType.Account })}
                  isLoading={isLoadingLogin()}
                  message={message()}
                />
              </Match>
              <Match when={loginType() === LoginType.MagicLink}>
                <MagicLinkSignIn
                  onLoginTypeChange={handleLoginTypeChange}
                  onSubmit={(email) => sendMagicLink({ email, companyId: getCompanyStyling()?.companyId })}
                  isLoading={isSendingMagicLink()}
                  message={message()}
                />
              </Match>
              <Match when={loginType() === LoginType.PhoneNumber}>
                <PhoneNumberSignIn
                  onLoginTypeChange={handleLoginTypeChange}
                  onSubmit={(phone, code) => {
                    if (code) {
                      logIn({ phone, loginToken: code, loginType: LoginType.PhoneNumber });
                    }
                  }}
                  onResendCode={(phone) => sendPhoneVerificationCode({ phone, domain: getCurrentHostName() })}
                  isLoading={isLoadingLogin() || isSendingPhoneVerificationCode()}
                  didSendCode={didSendPhoneVerificationCode()}
                  message={message()}
                />
              </Match>
            </Switch>
            <Show when={error()}>
              <div class="text-red-500">{error()}</div>
            </Show>
          </form>
        </div>
        <div class="absolute inset-x-0 bottom-0 bg-white py-7 text-center text-sm text-gray-600">
          {t(`{company} All Rights Reserved`, {
            company: `@${new Date().getFullYear()} ${getCompanyStyling()?.name}`,
          })}
        </div>
      </div>
    </div>
  );
};
